import { readableTextColor as baseReadableTextColor } from '@atlassian/color-picker/color-utils';

import type { NavigationStyle } from '../controllers/navigation-style-context';
import type { NavigationBarApiResponse } from '../types';

const readableTextColor = (color: string): string =>
	baseReadableTextColor(color, '@atlassian/company-hub-common');

export const DEFAULT_NAVIGATION_OPTIONS: NavigationStyle = {
	padding: true,
	fixedAtTop: false,
	backgroundColor: '#FFFFFF',
	textColor: '#172B4D',
	borderColor: '#DFE1E6',
	borderType: 'NO_BORDER',
	cornerRadius: 8,
};

/**
 * Returns the default navigation options resolved against a brand color.
 * When a brand color is provided, it is used as the default background color
 * and a contrasting text color is auto-derived, rather than falling back to the
 * hardcoded white/dark defaults.
 */
export function getDefaultNavigationOptions(brandColor?: string): NavigationStyle {
	if (!brandColor) {
		return DEFAULT_NAVIGATION_OPTIONS;
	}
	return {
		...DEFAULT_NAVIGATION_OPTIONS,
		backgroundColor: brandColor,
		textColor: readableTextColor(brandColor),
	};
}

export function mapApiToNavigationOptions(
	apiData: NavigationBarApiResponse,
	brandColor?: string,
	isCornerRadiusEnabled?: boolean,
): NavigationStyle {
	const defaults = getDefaultNavigationOptions(brandColor);

	if (!apiData) {
		return defaults;
	}

	return {
		padding: apiData.padding ?? defaults.padding,
		fixedAtTop: apiData.positionType === 'FIXED',
		backgroundColor: apiData.backgroundColor ?? defaults.backgroundColor,
		textColor: apiData.textColor ?? defaults.textColor,
		borderColor: apiData.borderColor ?? defaults.borderColor,
		borderType:
			apiData.borderType === 'BOTTOM_BORDER'
				? 'BOTTOM_BORDER'
				: apiData.borderType === 'ALL_BORDER'
					? 'ALL_BORDER'
					: 'NO_BORDER',
		// When the corner-radius feature is on, preserve a missing value as null so
		// the navbar inherits --hub-corner-radius. Otherwise keep the legacy 8 default.
		cornerRadius: isCornerRadiusEnabled
			? (apiData.cornerRadius ?? null)
			: (apiData.cornerRadius ?? defaults.cornerRadius),
	};
}
